/**
 * Abbonamenti sul computer, ridisegnati il 26/10/2026 (os-planning/ridisegno/CAPITOLATO.md).
 *
 * La domanda: quanto ci entra ogni mese dagli abbonamenti, da quali prodotti, e
 * cosa si rinnova a breve? Quindi, dall'alto:
 *  1. la risposta in una frase, al posto della fila di quattro numeri;
 *  2. i nostri prodotti come tessere col loro logo (GoDeal, Aura OS, Hosting,
 *     servizi su misura) piu' i canoni fatturati a mano: un clic filtra l'elenco;
 *  3. i rinnovi dei prossimi 30 giorni, giorno per giorno, e accanto le cose da
 *     sistemare (pagamenti non riusciti, attivi che non incassano...);
 *  4. l'elenco di tutti gli abbonamenti, ognuno col logo del prodotto;
 *  5. i canoni fatturati a mano (prima erano una scheda a parte).
 * Le tre schede di prima (Panoramica, Elenco, Fuori Stripe) diventano una pagina
 * sola che si legge scendendo. Sul telefono resta AbbonamentiMobile.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useModalStore } from '../../stores/modal.store';
import { toast } from '../../stores/toast.store';
import { Icon } from '../../components/ui/Icon';
import { Chip } from '../../components/ui/Chip';
import { NewMenu } from '../../components/ui/NewMenu';
import { ActionsMenu } from '../../components/ui/ActionsMenu';
import { EmptyState } from '../../components/ui/EmptyState';
import { Pagination } from '../../components/ui/Pagination';
import { BrandLogo } from '../../components/ui/BrandLogo';
import { SectionMark } from '../../components/ui/SectionMark';
import { OrdinaFiltra, useFiltri, useOrdina, type FiltroDef, type OpzioneOrdina } from '../../components/ui/OrdinaFiltra';
import { useUrlState } from '../../hooks/useUrlState';
import { fmtEur } from '../../lib/utils';
import { ManagedServiceType, ServiceStatus, SERVICE_STATUS_LABEL } from '@masamune/shared';
import type { Overview, OverviewRow } from './subscriptions.types';
import './subscriptions.css';
import './abbonamenti.css';

type Tipo = keyof typeof ManagedServiceType;

interface ManagedService {
  id: string;
  type: Tipo;
  status: keyof typeof ServiceStatus;
  billingCycle: 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  planLabel: string;
  amount: number;
  listAmount: number;
  currentPeriodEnd: string | null;
  metadata: Record<string, unknown>;
  client: { id: string; legalName: string; shortName: string } | null;
}

interface PaginatedServices {
  items: ManagedService[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

/** I nostri prodotti: il logo e il nome con cui li chiamiamo. */
const PRODOTTI: Array<{ type: Tipo; nome: string; brand: string | null; come: string }> = [
  { type: 'GODEAL', nome: 'GoDeal', brand: 'godeal', come: 'il CRM per i clienti' },
  { type: 'AURA_OS', nome: 'Aura OS', brand: 'auraos', come: 'il gestionale per gli studi medici' },
  { type: 'HOSTING', nome: 'Hosting', brand: 'hostinger', come: 'siti e caselle su Hostinger' },
  { type: 'CUSTOM', nome: 'Su misura', brand: null, come: 'servizi fatti apposta' },
];
const BRAND_DEL_TIPO: Record<string, string | null> = Object.fromEntries(PRODOTTI.map((p) => [p.type, p.brand]));
const NOME_DEL_TIPO: Record<string, string> = Object.fromEntries(PRODOTTI.map((p) => [p.type, p.nome]));

type Lente = 'senza-cliente' | 'scontati' | 'scaduti';
const LENTI: Lente[] = ['scaduti', 'scontati', 'senza-cliente'];
const LENTE_LABEL: Record<Lente, string> = {
  scaduti: 'Solo gli attivi col rinnovo già passato',
  scontati: 'Solo quelli con uno sconto per sempre',
  'senza-cliente': 'Solo gli attivi senza cliente in rubrica',
};

const ORDINI: OpzioneOrdina[] = [
  { key: 'stato', label: 'Prima gli attivi, in fondo i cancellati' },
  { key: 'rinnovo', label: 'Prossimo rinnovo prima' },
  { key: 'costosi', label: 'Prima i più costosi al mese' },
  { key: 'cliente', label: 'Cliente dalla A alla Z' },
];

const STATI: Array<{ key: keyof typeof ServiceStatus; label: string; conta: keyof Overview['counts'] }> = [
  { key: 'ACTIVE', label: 'Solo gli attivi', conta: 'active' },
  { key: 'PAST_DUE', label: 'Solo quelli col pagamento non riuscito', conta: 'pastDue' },
  { key: 'TRIALING', label: 'Solo quelli in prova gratuita', conta: 'trialing' },
  { key: 'PAUSED', label: 'Solo quelli in pausa', conta: 'paused' },
  { key: 'CANCELLED', label: 'Solo i cancellati', conta: 'cancelled' },
  { key: 'EXPIRED', label: 'Solo gli scaduti', conta: 'expired' },
];

function statoTone(s: ManagedService['status']): 'ok' | 'info' | 'warn' | 'alert' | 'ghost' {
  switch (s) {
    case 'ACTIVE': return 'ok';
    case 'TRIALING': return 'info';
    case 'PAST_DUE': return 'warn';
    case 'PAUSED': return 'ghost';
    default: return 'alert';
  }
}

function ciclo(c: ManagedService['billingCycle']): string {
  return c === 'MONTHLY' ? 'al mese' : c === 'QUARTERLY' ? 'ogni 3 mesi' : "l'anno";
}

const euro = (n: number) => `€ ${fmtEur(n)}`;
const chi = (r: OverviewRow) => r.clientName ?? r.stripeCustomerName ?? 'Senza anagrafica';

export function AbbonamentiDesktop() {
  const navigate = useNavigate();
  const openModal = useModalStore((s) => s.open);
  const qc = useQueryClient();
  const elencoRef = useRef<HTMLElement>(null);

  const [search, setSearch] = useUrlState<string>('q', '', { replace: true });
  // Gli abbonamenti gratuiti si nascondono e si vedono a richiesta (Lorenzo, 26/10/2026).
  const [gratis, setGratis] = useUrlState<'nascondi' | 'mostra'>('gratis', 'nascondi');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  const importStripe = useMutation({
    mutationFn: () => api.post<{ scanned: number; created: number; updated: number }>('/services/import-from-stripe'),
    onSuccess: (r) => {
      toast.ok('Abbonamenti riletti da Stripe', `${r.scanned} letti, ${r.created} nuovi, ${r.updated} aggiornati`);
      qc.invalidateQueries({ queryKey: ['services'] });
    },
    onError: (err) => toast.alert('Stripe non risponde', err instanceof ApiError ? err.message : 'Controlla la chiave di Stripe nelle impostazioni del server.'),
  });

  const { data: ov } = useQuery({ queryKey: ['services', 'overview'], queryFn: () => api.get<Overview>('/services/overview') });

  const ordina = useOrdina(ORDINI);
  const filtriDefs = useMemo<FiltroDef[]>(() => [
    { param: 'stato', label: "Stato dell'abbonamento", opzioni: STATI.map((s) => ({ value: s.key, label: s.label, count: ov?.counts[s.conta] })) },
    { param: 'tipo', label: 'Prodotto', opzioni: PRODOTTI.map((p) => ({ value: p.type, label: p.nome, count: ov ? (ov.byType.find((b) => b.type === p.type)?.total ?? 0) : undefined })) },
    {
      param: 'solo', label: 'Cose da sistemare',
      opzioni: LENTI.map((l) => ({
        value: l, label: LENTE_LABEL[l],
        count: !ov ? undefined
          : l === 'scaduti' ? ov.attention.overdue.length
          : l === 'scontati' ? ov.attention.discounted.length
          : ov.attention.noClientActive.length,
      })),
    },
  ], [ov]);
  const filtri = useFiltri(filtriDefs);
  const tipo = filtri.valore('tipo');
  const stato = filtri.valore('stato');
  const lenteParam = filtri.valore('solo');
  const lente: Lente | '' = (LENTI as string[]).includes(lenteParam) ? (lenteParam as Lente) : '';

  useEffect(() => { setPage(1); }, [filtri.chiave, search, ordina.key, gratis]);

  const idsLente = useMemo(() => {
    if (!ov || !lente) return null;
    const a = ov.attention;
    const src = lente === 'senza-cliente' ? a.noClientActive : lente === 'scontati' ? a.discounted : a.overdue;
    return new Set(src.map((r) => r.id));
  }, [ov, lente]);

  const params = new URLSearchParams();
  if (tipo) params.set('type', tipo);
  if (stato) params.set('status', stato);
  if (search) params.set('search', search);
  params.set('gratis', gratis === 'mostra' ? 'mostra' : 'nascondi');
  params.set('sort', ordina.key);
  params.set('page', String(idsLente ? 1 : page));
  params.set('pageSize', String(idsLente ? 200 : PAGE_SIZE));

  const { data, isLoading } = useQuery({
    queryKey: ['services', tipo, stato, search, ordina.key, page, !!idsLente, gratis],
    queryFn: () => api.get<PaginatedServices>(`/services?${params.toString()}`),
  });
  const righe = useMemo(() => {
    const items = data?.items ?? [];
    return idsLente ? items.filter((s) => idsLente.has(s.id)) : items;
  }, [data, idsLente]);
  const fatturati = useMemo(() => (ov?.reconciliation ? new Set(ov.reconciliation.invoicedServiceIds) : null), [ov]);
  const risultati = idsLente ? righe.length : data?.total ?? 0;

  const scendi = (ref: React.RefObject<HTMLElement>) => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  const mostraTipo = (t: Tipo) => { filtri.set('tipo', tipo === t ? [] : [t]); scendi(elencoRef); };
  const mostraLente = (l: Lente | 'PAST_DUE') => {
    if (l === 'PAST_DUE') { filtri.set('solo', []); filtri.set('stato', ['PAST_DUE']); } else filtri.set('solo', [l]);
    scendi(elencoRef);
  };

  // Le cose da sistemare, in ordine di quanto costano. Vuote = una riga che lo dice.
  const a = ov?.attention;
  const problemi = !a ? [] : [
    a.pastDue.length > 0 && { key: 'PAST_DUE' as const, grave: true, n: a.pastDue.length, titolo: a.pastDue.length === 1 ? 'pagamento non riuscito' : 'pagamenti non riusciti', perche: 'Stripe ha provato ad addebitare e non ci è riuscito: vanno sollecitati.' },
    a.overdue.length > 0 && { key: 'scaduti' as const, grave: true, n: a.overdue.length, titolo: a.overdue.length === 1 ? 'rinnovo già passato' : 'rinnovi già passati', perche: 'Il rinnovo non risulta: Stripe non si è aggiornato o l’addebito non è andato.' },
    a.noClientActive.length > 0 && { key: 'senza-cliente' as const, grave: false, n: a.noClientActive.length, titolo: a.noClientActive.length === 1 ? 'senza cliente in rubrica' : 'senza cliente in rubrica', perche: a.noClientActive.map((z) => z.stripeCustomerName ?? 'nome non disponibile').join(', ') },
    a.discounted.length > 0 && { key: 'scontati' as const, grave: false, n: a.discounted.length, titolo: a.discounted.length === 1 ? 'scontato per sempre' : 'scontati per sempre', perche: `${euro(a.discountYearly)} l'anno di differenza fra listino e incassato.` },
  ].filter(Boolean) as Array<{ key: Lente | 'PAST_DUE'; grave: boolean; n: number; titolo: string; perche: string }>;

  // I rinnovi dei prossimi 30 giorni, raggruppati per giorno.
  const giorni = useMemo(() => {
    const m = new Map<string, OverviewRow[]>();
    for (const r of ov?.renewals.next30.items ?? []) {
      const k = (r.currentPeriodEnd ?? '').slice(0, 10);
      m.set(k, [...(m.get(k) ?? []), r]);
    }
    return [...m.entries()];
  }, [ov]);

  return (
    <div className="page ab-page">
      {/* ── 1. La risposta ─────────────────────────────────────────────── */}
      <header className="ab-hd">
        <div className="ab-hd-main">
          <SectionMark icon="repeat" className="ab-sez" />
          <div className="min-w-0">
            <h1 className="ab-title">Abbonamenti</h1>
            {ov ? (
              <p className="ab-risposta">
                Ogni mese entrano <strong>{euro(ov.money.mrr)}</strong> da <strong>{ov.counts.paganti ?? ov.counts.vivi} abbonamenti</strong> pagati con
                {' '}<span className="ab-inline-logo"><BrandLogo brand="stripe" name="Stripe" size={20} /></span> Stripe.
                {ov.renewals.next30.count > 0 && (
                  <> Nei prossimi {ov.renewals.windowDays} giorni si rinnovano in {ov.renewals.next30.count}, per {euro(ov.renewals.next30.amount)}.</>
                )}
              </p>
            ) : (
              <p className="ab-risposta mute">Sto leggendo gli abbonamenti…</p>
            )}
          </div>
        </div>
        <div className="ab-hd-azioni">
          <NewMenu
            label="Nuovo abbonamento"
            groups={[{
              label: 'Abbonamenti',
              items: [
                { icon: 'link', title: 'Link di pagamento Stripe', sub: 'Il cliente paga e si abbona da solo', onClick: () => openModal('attachSubscription') },
                { icon: 'cube', title: 'Abbonamento a mano', sub: 'Senza Stripe, lo registriamo noi', onClick: () => openModal('newService') },
                { icon: 'refresh', title: importStripe.isPending ? 'Rileggo da Stripe…' : 'Rileggi da Stripe', sub: 'Aggiorna stati, importi e rinnovi', onClick: () => { if (!importStripe.isPending) importStripe.mutate(); } },
              ],
            }]}
          />
          <ActionsMenu variant="ghost" items={[{ key: 'export', label: 'Esporta', icon: 'export', onClick: () => openModal('export', { what: 'Servizi' }) }]} />
        </div>
      </header>

      {/* ── 2. I prodotti ──────────────────────────────────────────────── */}
      <section className="ab-prodotti" aria-label="I nostri prodotti">
        {PRODOTTI.map((p) => {
          const t = ov?.byType.find((b) => b.type === p.type);
          if (ov && (!t || t.total === 0)) return null;
          const acceso = tipo === p.type;
          return (
            <button key={p.type} type="button" className={`ab-prodotto${acceso ? ' is-on' : ''}`} onClick={() => mostraTipo(p.type)} aria-pressed={acceso}>
              <div className="ab-prodotto-hd">
                <BrandLogo brand={p.brand} name={p.nome} size={40} />
                <span className="ab-prodotto-via"><BrandLogo brand="stripe" name="Stripe" size={16} /> Stripe</span>
              </div>
              <div className="ab-prodotto-nome">{p.nome}</div>
              <div className="ab-prodotto-come">{p.come}</div>
              <div className="ab-prodotto-cifra">{t ? euro(t.mrr) : '…'}<span> al mese</span></div>
              <div className="ab-prodotto-sub">
                {t ? <>{t.paying ?? t.active} {(t.paying ?? t.active) === 1 ? 'cliente' : 'clienti'}{t.total > t.active ? `, ${t.total - t.active} chiusi nel tempo` : ''}</> : ' '}
              </div>
            </button>
          );
        })}
      </section>

      {/* ── 3. Rinnovi e cose da sistemare ─────────────────────────────── */}
      <div className="ab-cols">
        <section className="ab-blocco">
          <h2 className="ab-h2">
            <Icon name="calendarClock" size={15} /> Si rinnova nei prossimi {ov?.renewals.windowDays ?? 30} giorni
          </h2>
          {ov && giorni.length === 0 && <p className="ab-vuoto">Niente in scadenza nei prossimi {ov.renewals.windowDays} giorni.</p>}
          <div className="ab-agenda">
            {giorni.map(([g, rs]) => {
              const d = new Date(g);
              return (
                <div key={g} className="ab-giorno">
                  <div className="ab-giorno-data">
                    <span className="n">{d.getDate()}</span>
                    <span className="m">{d.toLocaleDateString('it-IT', { month: 'short' })}</span>
                  </div>
                  <div className="ab-giorno-voci">
                    {rs.map((r) => (
                      <button key={r.id} type="button" className="ab-rinnovo" onClick={() => navigate(`/services/${r.id}`)}>
                        <BrandLogo brand={BRAND_DEL_TIPO[r.type]} name={NOME_DEL_TIPO[r.type] ?? r.planLabel} size={28} />
                        <span className="ab-rinnovo-chi">
                          <span className="t">{chi(r)}</span>
                          <span className="s">{r.planLabel}</span>
                        </span>
                        <span className={`ab-rinnovo-cifra${r.amount === 0 ? ' zero' : ''}`}>{r.amount === 0 ? 'gratis' : euro(r.amount)}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <aside className="ab-blocco">
          <h2 className="ab-h2"><Icon name="alert" size={15} /> Da sistemare</h2>
          {ov && problemi.length === 0 && (
            <p className="ab-tuttook"><Icon name="check" size={14} /> Niente da sistemare: tutti gli attivi incassano e hanno un cliente.</p>
          )}
          <div className="ab-problemi">
            {problemi.map((p) => (
              <button key={p.key} type="button" className={`ab-problema${p.grave ? ' grave' : ''}`} onClick={() => mostraLente(p.key)}>
                <span className="ab-problema-n">{p.n}</span>
                <span className="ab-problema-tx">
                  <span className="t">{p.titolo}</span>
                  <span className="s">{p.perche}</span>
                </span>
                <Icon name="chevronR" size={12} className="mute" />
              </button>
            ))}
          </div>
        </aside>
      </div>

      {/* ── 4. Tutti gli abbonamenti ───────────────────────────────────── */}
      <section className="ab-blocco ab-elenco" ref={elencoRef}>
        <div className="ab-elenco-hd">
          <h2 className="ab-h2"><Icon name="list" size={15} /> Tutti gli abbonamenti</h2>
          <div className="ab-seg" role="tablist" aria-label="Prodotto">
            <button type="button" role="tab" aria-selected={!tipo} className="ab-seg-btn" onClick={() => filtri.set('tipo', [])}>Tutti</button>
            {PRODOTTI.filter((p) => !ov || (ov.byType.find((b) => b.type === p.type)?.total ?? 0) > 0).map((p) => (
              <button key={p.type} type="button" role="tab" aria-selected={tipo === p.type} className="ab-seg-btn" onClick={() => filtri.set('tipo', tipo === p.type ? [] : [p.type])}>
                <BrandLogo brand={p.brand} name={p.nome} size={18} /> {p.nome}
              </button>
            ))}
          </div>
        </div>
        <div className="filterbar filterbar--float mb-3 flex-wrap">
          <div className="tb-search" style={{ width: 300, margin: 0 }}>
            <Icon name="search" size={12} />
            <input aria-label="Cerca fra gli abbonamenti" placeholder="Cerca per cliente o piano…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="spacer" />
          <span className="mute text-[12px]"><span className="mono">{risultati}</span> {risultati === 1 ? 'abbonamento' : 'abbonamenti'}</span>
          {(ov?.counts.gratis ?? 0) > 0 && (
            <button type="button" className={`ab-gratis${gratis === 'mostra' ? ' is-on' : ''}`} onClick={() => setGratis(gratis === 'mostra' ? 'nascondi' : 'mostra')} aria-pressed={gratis === 'mostra'}>
              <Icon name={gratis === 'mostra' ? 'eye' : 'eyeOff'} size={13} />
              {gratis === 'mostra' ? `Nascondi i ${ov!.counts.gratis} gratuiti` : `Mostra anche i ${ov!.counts.gratis} gratuiti`}
            </button>
          )}
          <OrdinaFiltra ordina={ordina} filtri={filtri} />
        </div>

        {lente && (
          <div className="adm-note">
            <Icon name="filter" size={12} className="mute" />
            <span>
              {LENTE_LABEL[lente]}.{' '}
              <button className="ab-link" onClick={() => filtri.set('solo', [])}>Mostra tutti</button>
            </span>
          </div>
        )}

        <div className="ab-righe">
          {isLoading && !data && <p className="ab-vuoto">Carico gli abbonamenti…</p>}
          {righe.map((s) => {
            const nomeStripe = (s.metadata?.stripeCustomerName as string) ?? null;
            const scontato = s.listAmount > s.amount;
            return (
              <div key={s.id} className="ab-riga" role="button" tabIndex={0} onClick={() => navigate(`/services/${s.id}`)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/services/${s.id}`); }}>
                <BrandLogo brand={BRAND_DEL_TIPO[s.type]} name={NOME_DEL_TIPO[s.type] ?? s.planLabel} size={32} />
                <span className="ab-riga-chi">
                  {s.client ? (
                    <Link to={`/contatti/client/${s.client.id}`} className="t" onClick={(e) => e.stopPropagation()}>{s.client.shortName || s.client.legalName}</Link>
                  ) : (
                    <span className="t">{nomeStripe ?? 'Senza anagrafica'} <Chip tone="warn">non in rubrica</Chip></span>
                  )}
                  <span className="s">{s.planLabel}</span>
                </span>
                <span className="ab-riga-stato"><Chip tone={statoTone(s.status)} dot>{SERVICE_STATUS_LABEL[s.status]}</Chip></span>
                <span className="ab-riga-cifra">
                  {s.amount === 0 ? <span className="text-alert">gratis</span> : <>{euro(s.amount)} <span className="mute">{ciclo(s.billingCycle)}</span></>}
                  {scontato && s.amount > 0 && <span className="ab-listino">{euro(s.listAmount)}</span>}
                </span>
                <span className="ab-riga-rinnovo">
                  {s.currentPeriodEnd && (s.status === 'ACTIVE' || s.status === 'PAST_DUE' || s.status === 'TRIALING')
                    ? <>si rinnova il {new Date(s.currentPeriodEnd).toLocaleDateString('it-IT', { day: 'numeric', month: 'short', year: 'numeric' })}</>
                    : <span className="mute">non si rinnova</span>}
                </span>
                {fatturati && (
                  <span className="ab-riga-fattura" title="Fattura del periodo in corso">
                    {s.status === 'ACTIVE' && s.amount > 0
                      ? (fatturati.has(s.id) ? <><Icon name="check" size={12} /> fatturato</> : <span className="mute">fattura da fare</span>)
                      : null}
                  </span>
                )}
                <Icon name="chevronR" size={12} className="mute" />
              </div>
            );
          })}
          {!isLoading && righe.length === 0 && (
            <EmptyState
              brand
              title={filtri.attivi > 0 || search ? 'Nessun abbonamento trovato' : 'Nessun abbonamento ancora'}
              description={filtri.attivi > 0 || search ? 'Prova a togliere un filtro o a cambiare la ricerca.' : 'Rileggi gli abbonamenti da Stripe oppure registrane uno a mano.'}
            />
          )}
        </div>
        {data && !idsLente && data.totalPages > 1 && (
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} totalPages={data.totalPages} onPage={setPage} />
        )}
        {fatturati && ov?.reconciliation && (
          <p className="ab-nota">
            <Icon name="help" size={12} /> «Fattura da fare» vuol dire che per il periodo in corso non c'è una fattura dello stesso importo intestata al cliente.
            Le fatture automatiche partono da {new Date(ov.reconciliation.since).toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}: per gli annuali partiti prima è normale.
          </p>
        )}
      </section>

    </div>
  );
}
